/**
 * CasePane: a stored case, rendered for the narrow Outlook task pane.
 *
 * Used when the open Outlook message is already in the case store (found by its
 * `internetMessageId`), and by the local-dev standalone demo. Outlook shows the mail
 * beside the pane, so the pane is the work column of the case page and nothing else
 * (`CaseWork` with `variant="pane"` and `layout="list"`): the customer line with its
 * one-click confirm, the draft lines as cards with one status icon and word each, in
 * triage order, the one action with the reply text above it, then "Details (n)" and
 * "Open in I2Q". The 400 px pane cannot hold the table of the case page, so it keeps
 * the list form with the same icons.
 *
 * Lines, items and the reply mean here what they mean in the workbench (review F05/F06,
 * 11 Oct 2026): the same components, the same helpers (`line-semantics`).
 *
 * Read-only towards the mailbox: there is no compose, reply or send action here, no
 * Outlook draft is created, and the add-in reads the open item only. What the pane
 * writes (a confirmed customer, a line decision, a status) goes to the I2Q database
 * only. "Copy the reply" copies the text shown under the button; nothing is sent.
 */

import { Card, ThemeProvider } from "@norican/react";
import { useNavigate } from "react-router";

import { NoricanLogo } from "@/components/norican-logo";
import { ShadowModeBadge } from "@/components/shadow-mode-badge";
import { useAppTheme } from "@/contexts/theme";
import { stripCustomerOf } from "@/features/case-review/case-summary-strip";
import { CaseWork } from "@/features/case-review/case-work";
import { logger } from "@/lib/logger";
import { type QuoteCaseDetail } from "@/lib/mock";
import { track } from "@/lib/usage";

/** The customer as the pane reads it: a suggestion says it waits for a confirmation. */
export function paneCustomerText(detail: QuoteCaseDetail): string {
  const customer = stripCustomerOf(detail);
  if (customer.state === "certain") {
    return customer.name || detail.customer;
  }
  if (customer.state === "suggested") {
    const first = customer.options[0];
    const name = first?.name ?? (first ? `Account ${first.account}` : detail.customer);
    return `${name}, to confirm`;
  }
  return "Not identified";
}

/** The product name next to the Norican logo: the logo already says Norican. */
export const PANE_PRODUCT = "I2Q";

/** The pane header: Norican mark + product name + a subtitle line. */
export function PaneHeader({ subtitle, aside }: { subtitle: string; aside?: React.ReactNode }) {
  return (
    <Card.Header className="border-border-subtle flex items-center gap-2.5 border-b">
      {/* Secondary logo (no tagline, guide p.14), not the icon mark: the guide keeps the
          mark as support to the logo, standalone only as avatar or favicon (p.15). At a
          20 px box it is 11 px of artwork (minimum 10 px, p.17) and about 76 px wide,
          which fits the narrowest Outlook pane. Theme-aware: Norican Blue on the light
          card, white in dark mode. */}
      <NoricanLogo kind="secondary" height={20} />
      <div className="min-w-0 flex-1 leading-tight">
        <div className="text-fg text-[0.8125rem] font-bold">{PANE_PRODUCT}</div>
        <div className="text-fg-subtle truncate text-xs">{subtitle}</div>
      </div>
      {aside}
    </Card.Header>
  );
}

/** The shadow-mode note of the live preview pane (the case pane says it on its Copy caption). */
export function PaneShadowNote() {
  return (
    <div className="flex justify-center">
      <ShadowModeBadge label="Shadow mode: nothing is sent" size="xs" />
    </div>
  );
}

export interface CasePaneProps {
  detail: QuoteCaseDetail;
  /** Extra classes for the pane wrapper (e.g. a fixed width beside the demo mail). */
  className?: string;
}

export function CasePane({ detail, className }: CasePaneProps) {
  const navigate = useNavigate();
  const { theme } = useAppTheme();

  const openInWorkbench = () => {
    logger.debug("[addin] open the case in I2Q", { case: detail.id });
    track("addin.open_in_i2q", {}, { caseId: detail.id });
    navigate(`/case/${detail.id}`);
  };

  // Compact density scoped to the narrow task pane; theme is inherited from the app.
  return (
    <ThemeProvider theme={theme} density="compact" className={className ?? "w-full"}>
      <Card variant="outline" className="overflow-hidden">
        <PaneHeader subtitle={`case ${detail.ref}`} />
        <Card.Body>
          {/* Keyed per case: a "Copied" or an open sheet never carries over to the next mail. */}
          <CaseWork
            key={detail.id}
            detail={detail}
            variant="pane"
            layout="list"
            onOpenInI2q={openInWorkbench}
          />
        </Card.Body>
      </Card>
    </ThemeProvider>
  );
}
